import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Loader2 } from "lucide-react";

interface GeocodingResult {
  name: string;
  latitude: number;
  longitude: number;
  country: string;
  admin1?: string;
  timezone?: string;
}

interface LocationSearchProps {
  onLocationSelect: (lat: number, lon: number, name: string, timezone?: string) => void;
}

export function LocationSearch({ onLocationSelect }: LocationSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    try {
      // Use Open-Meteo Geocoding API
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(searchQuery)}&count=5&language=en&format=json`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch location data');
      }
      const data = await response.json();
      if (!data.results || data.results.length === 0) {
        setSearchError('No locations found. Try a different search term.');
        setSearchResults([]);
      } else {
        setSearchResults(data.results);
      }
    } catch {
      // Search error (console removed)
      setSearchError('Unable to search locations. Please check your connection and try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSelectLocation = (result: GeocodingResult) => {
    onLocationSelect(result.latitude, result.longitude, result.name, result.timezone);
    setSearchQuery("");
    setSearchResults([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2" role="search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            type="text"
            placeholder="Search for a city or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            className="pl-10"
            aria-label="Search for a city or location"
            aria-describedby={searchError ? "search-error" : undefined}
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={isSearching}
          aria-label={isSearching ? "Searching for location..." : "Search location"}
          aria-busy={isSearching}
        >
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            "Search"
          )}
        </Button>
      </div>

      {searchError && (
        <div
          id="search-error"
          className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm"
          role="alert"
          aria-live="polite"
        >
          {searchError}
        </div>
      )}

      {searchResults.length > 0 && (
        <div
          className="bg-card border border-border rounded-lg shadow-lg overflow-hidden"
          role="listbox"
          aria-label="Location search results"
        >
          {searchResults.map((result, index) => (
            <button
              key={index}
              onClick={() => handleSelectLocation(result)}
              className="w-full px-4 py-3 text-left hover:bg-muted transition-colors flex items-center gap-3 border-b border-border last:border-b-0"
              role="option"
              aria-selected={false}
            >
              <MapPin className="h-4 w-4 text-primary flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{result.name}</div>
                <div className="text-sm text-muted-foreground truncate">
                  {result.admin1 && `${result.admin1}, `}
                  {result.country}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {result.latitude.toFixed(2)}, {result.longitude.toFixed(2)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
